import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Tooltip from '@mui/material/Tooltip';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';

import PolicyCard from '../../components/policycard';
import ColorSelector from '../../components/colorselector';
import SearchBar from '../../components/searchbar';
import ConfirmationDialog from '../../components/confirmationdlg';
import Validator, { useValidate, checkValidity } from '../../components/validator';

import PolicyService from '../../data/policy-service';
import Auth from '../../data/auth-service';
import SuricataService from '../../data/suricata-service';

const useStyles = makeStyles(theme => ({
    accordionStrip: {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    buttonMargin: {
        marginLeft: theme.spacing(2),
    },
    cleanLink: {
        textDecoration: 'none',
    },
    highlightText: {
        color: theme.palette.secondary.main,
    },
    busy: {
        padding: 10,
        fontSize: 'small',
        color: theme.palette.tertiary.main,
    }
}));

const empty = { type: '', name: '', color: '' };

const ptypes = [
    { route: 'sensor/bt_conf', type: 'btconf' },
    { route: 'sensor/data_tuning', type: 'data_tags' },
    { route: 'sensor/exports', type: 'eexportd_conf' },
    { route: 'sensor/file_hash', type: 'file_hash' },
    { route: 'sensor/geo_ip', type: 'custom_geoip' },
    { route: 'sensor/pcap', type: 'pcap_config' },
    { route: 'sensor/configuration', type: 'system_config' },
    { route: 'sensor/upgrade', type: 'sensor_upgrade' },
    { route: 'zeek/scripts', type: 'bro_ti' },
    { route: 'zeek/bt_scripts', type: 'bt_bro_ti' },
    { route: 'zeek/zeek_indicators', type: 'zeek_indicators' },
    { route: 'suricata/rules', type: 'suricata_rules' },
    { route: 'suricata/bt_rules', type: 'bt_suricata_manual' },
    { route: 'suricata/vars', type: 'suricata_config' },
];

const PolicySensorSelector = () => {
    const classes = useStyles();
    const location = useLocation();
    const [policyType, setPolicyType] = useState('');
    const [policies, setPolicies] = useState([]);
    const [policy, setPolicy] = useState(false);
    const [edit, setEdit] = useState(false);
    const [policyData, setPolicyData] = useState(_cloneDeep({ ...empty }));
    const [editData, setEditData] = useState(_cloneDeep({ ...empty }));
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [ruleCount, _setRuleCount] = useState({});
    const ruleCountRef = useRef();
    const policyForm = useValidate({});
    const [managedStatus, setManagedStatus] = useState(false);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef();
    const cancelBusyRef = useRef();

    const policyPerms = {
        bro_ti: Auth.permissions.get('ZeekScriptsPolicy'),
        custom_geoip: Auth.permissions.get('GeoIPPolicy'),
        eexportd_conf: Auth.permissions.get('ExportsPolicy'),
        pcap_config: Auth.permissions.get('PcapPolicy'),
        data_tags: Auth.permissions.get('DataTuningPolicy'),
        file_hash: Auth.permissions.get('FileHashPolicy'),
        system_config: Auth.permissions.get('ConfigPolicy'),
        sensor_upgrade: Auth.permissions.get('UpgradePolicy'),
        suricata_rules: Auth.permissions.get('SuricataRulesPolicy'),
        suricata_config: Auth.permissions.get('SuricataVarsPolicy'),
        zeek_indicators: Auth.permissions.get('ThreatIntelPolicy'),
    };
    const suriSettingsPerms = Auth.permissions.get('SuricataRules');
    const zeekSettingsPerms = Auth.permissions.get('ZeekScripts');
    const tiSettingsPerms = Auth.permissions.get('ThreatIntelIndicators');

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    const setRuleCount = (state) => {
        ruleCountRef.current = state;
        _setRuleCount(state);
    };

    useEffect(() => {
        SuricataService.isManaged().then(res => {
            setManagedStatus(res.managed);
        }, err => {
            console.error('Unable to check managed status ::: ', err);
        });
        return () => {
            if (cancelBusyRef.current) {
                clearTimeout(cancelBusyRef.current);
            }
        };
    }, []);

    useEffect(() => {
        ptypes.forEach((v) => {
            if (location.pathname.includes(v.route)) {
                setPolicyType(v.type);
            }
        });
    }, [location]);

    useEffect(() => {
        if (policyType && policyPerms[policyType].read) {
            updatePolicies();
            // initial busy state of the policy list
            getBusy();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyType]);

    const updatePolicies = (search) => {
        PolicyService.getPolicies(policyType).then(res => {
            if (search) {
                search(res);
            } else {
                setPolicies(res);
            }

            if (policyType === 'suricata_rules' || policyType === 'bt_suricata_manual') {
                if (res.length) {
                    thisIsGross(policyType, res.length - 1, res);
                }
            }

            // we might be able to stop busy polling
            if (!res.some(p => p.pending)) {
                setBusy(false);
            }
        }, err => {
            console.error('Unable to get policies ::: ', err);
        });
    };

    const thisIsGross = (type, i, res) => {
        if (!/\.mc2$/.test(res[i].name)) {
            SuricataService.listPolicyGroups(
                res[i].name,
                type,
                { page: 0, limit: 1, search: '', sort: '', mod: { stack: [] } }
            ).then(r => {
                setRuleCount({
                    ...ruleCountRef.current,
                    [res[i].name]: r.enabled_enabled_rules,
                });
                i--;
                if (i > -1) {
                    thisIsGross(type, i, res);
                }
            });
        } else {
            i--;
            if (i > -1) {
                thisIsGross(type, i, res);
            }
        }
    };

    const toggleNewPolicy = () => {
        setPolicy(!policy);
    };

    const handleUpdatePolicyName = (e, validID, valid) => {
        if (validID) {
            policyForm.set({
                ...policyForm.get(),
                [validID]: valid
            });
        }

        setPolicyData({
            ...policyData,
            name: e.target.value.replace(' ', ''),
        });
    };

    const handleEditPolicy = (p) => {
        setPolicy(true);
        setPolicyData({
            ...p,
            name: p.name.replace('.mc2', ''),
        });
        setEdit(true);
        setEditData({ ...p });
    };

    const handleColorSelect = (c) => {
        setPolicyData({
            ...policyData,
            color: c,
        });
    };

    const handleCancelPolicy = () => {
        setPolicy(false);
        setEdit(false);
        setPolicyData(_cloneDeep({ ...empty }));
        setEditData(_cloneDeep({ ...empty }));
    };

    const handleSubmitPolicy = () => {
        if (edit) {
            PolicyService.updatePolicy(policyType, editData.name, {
                name: policyData.name,
                color: policyData.color,
                type: policyType
            }).then(res => {
                updatePolicies();
                if (res?.data?.policy?.not_ready) {
                    setBusy(true);
                    getBusy();
                }
            }, err => {
                console.error(err);
                setBusy(false);
            });
        } else {
            PolicyService.createPolicy(policyType, policyData.name, policyData.color).then((res) => {
                updatePolicies();
                if (res?.data?.policy?.stale) {
                    setBusy(true);
                    getBusy();
                }
            }, err => {
                console.error(err);
                toast.error(`Cannot create a policy with the name '${policyData.name}'`);
                setBusy(false);
            });
        }
        handleCancelPolicy();
    };

    const getBusy = () => {
        // guard against concurrent calls to get busy
        if (busyRef.current) {
            return;
        }
        PolicyService.getBusy(policyType).then(res => {
            setBusy(res.busy);
            if (res.busy) {
                cancelBusyRef.current = setTimeout(getBusy, 4200);
            } else {
                updatePolicies();
                busyRef.current = undefined;
            }
        }, err => {
            console.error(err);
            setBusy(false);
            busyRef.current = undefined;
        });
    };

    const handleSearchChange = ({ search }) => {
        updatePolicies((p) => {
            p = p.filter(x => x.name.toLowerCase().match((search || '').toLowerCase()));
            setPolicies(p);
        });
    };

    const handleOpenDelete = (p) => {
        setSelectedPolicy(p);
        setDeleteOpen(true);
    };

    const handleCancelDelete = () => {
        setSelectedPolicy(null);
        setDeleteOpen(false);
    };

    const handleDeletePolicy = (p) => {
        PolicyService.deletePolicy(policyType, selectedPolicy.name).then(() => {
            setSelectedPolicy(null);
            setDeleteOpen(false);
            updatePolicies();
            toast.success('Policy deleted successfully');
        }, (err) => {
            console.error(err);
            toast.error('An error occurred deleting the policy...');
            setSelectedPolicy(null);
            setDeleteOpen(false);
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                    <SearchBar immediate onSearch={handleSearchChange} searchservice={PolicyService} style={{ width: 350 }}/>
                </Box>
                <Box display="flex">
                    {tiSettingsPerms.read && busy &&
                      <Box className={classes.busy}>
                          Policy update in progress
                          <SyncIcon style={{ paddingLeft: 5, verticalAlign: 'middle', cursor: 'pointer' }} fontSize="small" />
                      </Box>
                    }
                    {(policyPerms[policyType]?.write || false) && <Box>
                        <Button aria-label="add policy" variant="contained" color="primary" onClick={toggleNewPolicy} disabled={busy}>
                            Create Policy
                        </Button>
                    </Box>}
                    {tiSettingsPerms.read && <Box display={policyType === 'zeek_indicators' ? 'block' : 'none'}>
                        <Link className={classes.cleanLink} aria-label="settings" to={'/policies/zeek/zeek_indicators/settings'}>
                            <Button className={classes.buttonMargin} aria-label="settings" variant="contained" color="primary">
                                <SettingsIcon />
                                TI Settings
                            </Button>
                        </Link>
                    </Box>}
                    <Box display={policyType === 'bro_ti' ? 'block' : 'none'}>
                        {policyPerms.bro_ti.read && <Link className={classes.cleanLink} aria-label="edit" to={'/policies/zeek/scripts/editor'}>
                            <Button className={classes.buttonMargin} aria-label="edit" variant="contained" color="primary">
                                <EditIcon />
                                Script Editor
                            </Button>
                        </Link>}
                        {zeekSettingsPerms.read && <Link className={classes.cleanLink} aria-label="settings" to={'/policies/zeek/scripts/settings'}>
                            <Button className={classes.buttonMargin} aria-label="settings" variant="contained" color="primary">
                                <SettingsIcon />
                                Zeek Settings
                            </Button>
                        </Link>}
                    </Box>
                    {suriSettingsPerms.read && <Box display={policyType === 'suricata_rules' ? 'flex' : 'none'} justifyContent="flex-end">
                        {managedStatus
                            ? <Tooltip title="Rules must be managed by the MC2.">
                                <span>
                                    <Button className={classes.buttonMargin} disabled aria-label="editor" variant="contained" color="primary">
                                        <EditIcon />
                                        Rules Editor
                                    </Button>
                                </span>
                            </Tooltip>
                            : <Link
                                className={classes.cleanLink}
                                aria-label="editor"
                                to={'/policies/suricata/rules/editor'}
                                state={ { prevPath: '/policies/suricata/rules' } }
                            >
                                <Button className={classes.buttonMargin} aria-label="editor" variant="contained" color="primary">
                                    <EditIcon />
                                    Rules Editor
                                </Button>
                            </Link>
                        }
                        <Link className={classes.cleanLink} aria-label="settings" to={'/policies/suricata/rules/settings'}>
                            <Button className={classes.buttonMargin} aria-label="settings" variant="contained" color="primary">
                                <SettingsIcon />
                                Suricata Settings
                            </Button>
                        </Link>
                    </Box>}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Accordion expanded={policy} display={policy ? 'flex' : 'none'} className={classes.accordionStrip}>
                    <AccordionSummary style={{ display: 'none' }} />
                    <AccordionDetails>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid container item xs={9}>
                                <Grid item xs={12}>
                                    <Validator
                                        valueType="policyName"
                                        errorText="Policy name invalid"
                                        fullWidth
                                        placeholder="Policy name"
                                        onChange={handleUpdatePolicyName}
                                        value={policyData.name}
                                    />
                                </Grid>
                                <Grid item container xs={12} justifyContent={'flex-end'}>
                                    {(policyPerms[policyType]?.write || false) && <Box mt={2} display="flex">
                                        <Box><Button
                                            variant="contained"
                                            color="secondary"
                                            aria-label="Cancel"
                                            onClick={handleCancelPolicy}
                                        >
                                            Cancel
                                        </Button></Box>
                                        <Box ml={2}><Button
                                            variant="contained"
                                            color="primary"
                                            aria-label={edit ? 'Update' : 'Create'}
                                            onClick={handleSubmitPolicy}
                                            disabled={!(checkValidity(policyForm.get()))}
                                        >
                                            {edit ? 'Update' : 'Create'}
                                        </Button></Box>
                                    </Box>}
                                </Grid>
                            </Grid>
                            <Grid container item xs={3}>
                                <Grid item xs={12} style={{ minWidth: 200 }}>
                                    <ColorSelector onSelectColor={handleColorSelect} color={policyData.color || ''} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>
            <Grid container item xs={12} spacing={2}>
                {policies.map((p, i) => {
                    const rc = ruleCount[p.name];
                    return (
                        <Grid item xs={4} key={'policy-' + i}>
                            <PolicyCard
                                title={p.name}
                                subtitle={<Box>
                                    <Box display={MC2CHECK ? 'none' : 'block'}>Source: {p.source}</Box>
                                    <Box>Sensors: {p.count}</Box>
                                    <Box display={typeof rc === 'number' ? 'block' : 'none'}>
                                        Rules: {rc}
                                    </Box>
                                </Box>}
                                route={`/policies/${ptypes[ptypes.findIndex(v => v.type === policyType)].route}/${p.name}`}
                                action={<Box p={1} display={p.name === 'default' || ((!MC2CHECK && p.source === 'mc2') && p.count) || p.pending || busy ? 'none' : 'block'}>
                                    {policyPerms[policyType]?.write && <Box display={!MC2CHECK && p.source === 'mc2' ? 'none' : 'block'}>
                                        <IconButton
                                            aria-label="edit policy"
                                            onClick={() => { handleEditPolicy(p) }}
                                            size="large"><SettingsIcon /></IconButton>
                                    </Box>}
                                    {policyPerms[policyType]?.delete && <Box display={p.count ? 'none' : 'block'}>
                                        <IconButton
                                            color="secondary"
                                            aria-label="delete policy"
                                            onClick={() => { handleOpenDelete(p) }}
                                            size="large"><DeleteIcon /></IconButton>
                                    </Box>}
                                </Box>}
                                color={p.color || ''}
                                type={policyType || ''}
                                source={p.source}
                                pending={p.pending}
                                busy={p.busy}
                            />
                        </Grid>
                    );
                })}
            </Grid>
            <ConfirmationDialog
                onClose={handleCancelDelete}
                onOK={handleDeletePolicy}
                open={deleteOpen}
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to delete the policy <span className={classes.highlightText}>{(selectedPolicy && selectedPolicy.name) || ''}</span>?
                        </Box>
                    );
                })()}
                title="Delete Policy"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default PolicySensorSelector;
